import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Education, ProfessionalExperience, References } from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import config from '../../../config';
import Utils from '../../../utils/Utils';
import QRCode from 'qrcode';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import { OrganizationView } from '../TemplateOrganizationComp';
import { VolunteeringView } from '../TemplateVolunteeringComp';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getReactPdfFont());
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getOpenSansFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  if (color === '#080808') {
    color1 = '#1A1A1A';
    color2 = '#2B2B2B';
  }
  if (color === '#042357') {
    color1 = '#1D3865';
    color2 = '#344869';
  }
  if (color === '#022715') {
    color1 = '#4C6559';
    color2 = '#667B71';
  }
  if (color === '#333434') {
    color1 = '#505050';
    color2 = '#6F6F6F';
  }
  return { color1, color2 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      flex: 1,
      flexDirection: 'column',
      paddingBottom: 30,
      paddingTop: 20,
    },
    title: {
      fontSize: 22,
      color: props?.color ?? '#000',
      fontFamily: 'OpenSans',
      fontWeight: 'bold',
      paddingBottom: 10,
      flexWrap: 'wrap',
    },
    author: {
      fontSize: 12,
      textAlign: 'center',
      marginBottom: 40,
    },
    subtitle: {
      fontSize: 18,
      margin: 12,
      fontFamily: 'Inter',
    },
    name: {
      fontSize: 36,
      color: props?.color ?? '#000',
      fontFamily: 'Inter',
      fontWeight: 'bold',
    },
    designation: {
      fontSize: 13,
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
      fontFamily: 'OpenSans',
      paddingTop: 6,
      fontWeight: 'normal',
    },
    designationContainer: {
      backgroundColor: '#B0FFF7',
      paddingBottom: 5,
      paddingRight: 5,
      paddingLeft: 5,
      marginTop: 10,
      marginBottom: 10,
    },
    country: {
      fontSize: 18,
      color: props?.color ? colors(props.color).color2 : '#2B2B2B',
      fontFamily: 'Inter',
      paddingTop: 6,
    },
    linkTitle: {
      fontSize: 12,
      color: '#393838',
      fontFamily: 'Inter',
      textAlign: 'left',
      fontWeight: 'medium',
    },
    linkText: {
      fontSize: 12,
      textAlign: 'left',
      lineHeight: 1.5,
      color: '#150F0F',
      fontFamily: 'Inter',
    },
    text: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      color: props?.color ? colors(props.color).color2 : '#2B2B2B',
      fontFamily: 'Inter',
    },
    contactText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1,
      color: '#2A2828',
      fontFamily: 'Inter',
      paddingRight: -10,
      maxWidth: 155,
    },
    emailText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1,
      color: '#2A2828',
      fontFamily: 'Inter',
      paddingRight: -10,
      maxWidth: 155,
      textDecoration: 'none',
    },
    descText: {
      fontSize: 10,
      textAlign: 'left',
      color: '#5F5F5F',
      fontFamily: 'Inter',
    },
    image: {
      marginVertical: 15,
      marginHorizontal: 100,
    },
    header: {
      fontSize: 12,
      marginBottom: 20,
      textAlign: 'center',
      color: 'grey',
    },
    pageNumber: {
      position: 'absolute',
      fontSize: 12,
      bottom: 20,
      left: 0,
      right: 0,
      textAlign: 'center',
      color: 'grey',
    },
    staticContent: {
      position: 'absolute',
      fontSize: 12,
      bottom: 20,
      left: 100,
      right: 0,
      textAlign: 'center',
      color: '#241313',
    },
    headerBar: {
      // position: 'absolute',
      fontSize: 12,
      top: 15,
      left: 0,
      right: 0,
      height: 10,
      textAlign: 'center',
      color: 'grey',
    },
    colomn2: {
      backgroundColor: '#FFFFFF',
      flex: 2,
      padding: 22,
    },
    colomn1: {
      backgroundColor: '#FFFF',
      flex: 1,
      padding: 22,
    },
    durationBox: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    dateText: {
      color: '#2A2828',
      fontSize: 10,
    },
    timeline: {
      color: '#5F5F5F',
      fontSize: 10,
      fontFamily: 'Inter',
    },
    exTitle: {
      fontSize: 14,
      textAlign: 'left',
      lineHeight: 1.5,
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
      fontFamily: 'Inter',
      paddingTop: 6,
      fontWeight: 'bold',
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: 10,
    },
    textInstitute: {
      fontSize: 12,
      textAlign: 'left',
      lineHeight: 1.5,
      color: '#4F4F4F',
      fontFamily: 'Inter',
    },
    durationContainer: {
      flex: 1,
      borderStyle: 'solid',
      borderLeftWidth: 1,
      marginLeft: 10,
      paddingLeft: 10,
      justifyContent: 'center',
      marginBottom: 10,
    },
    tag: {
      backgroundColor: '#E5EAEC',
      minHeight: 22,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 8,
      padding: 5,
      marginRight: 10,
      marginBottom: 8,
    },
    refName: {
      fontSize: 16,
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
      fontFamily: 'Inter',
      paddingTop: 8,
      marginBottom: 4,
      fontWeight: 'medium',
    },
    boldText: {
      fontSize: 13,
      textAlign: 'left',
      lineHeight: 1.5,
      fontWeight: 'bold',
      color: props?.color ? colors(props.color).color2 : '#2B2B2B',
      fontFamily: 'Inter',
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string }> = ({
  value,
  selectedColor,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const durationFrom = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    return fromStr;
  }, [value.period.from]);

  const durationTo = useMemo(() => {
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return toStr;
  }, [value.period.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      <View style={styles.durationBox}>{/*<Text style={styles.dateText}>lllllllll</Text>*/}</View>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <View style={{ flex: 5 }}>
          <Text style={styles.exTitle}>{value.designation.value}</Text>
          <Text style={styles.text}>{`${value.company.value ?? ''} | ${value.city ?? ''} ${
            value.city ? ',' : ''
          } ${value.country ?? ''}`}</Text>
        </View>
        <View style={styles.durationContainer}>
          <Text style={styles.dateText}>{durationFrom}</Text>
          <Text style={styles.dateText}>{durationTo}</Text>
        </View>
      </View>
      <View>
        {value.description && (
          <PdfHtml level={2}>
            {value.description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        )}
      </View>
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string }> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title, description } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginBottom: 10 }}>
      {/*<Text style={styles.exTitle}>*/}
      {/*  {type === 'Certification' ? results[0]?.subject?.value ?? type : type}*/}
      {/*</Text>*/}
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}>
        <View style={{ display: 'flex', flexDirection: 'column', flex: 5 }}>
          <View>
            {type === 'Certification' ? (
              <>
                <View>
                  <Text style={styles.exTitle}>{`${results[0]?.subject?.value ?? type}`}</Text>
                </View>
                {/*{results[0]?.result ? (*/}
                {/*  <Text style={styles.text}>{`Certification ID : ${results[0]?.result}`}</Text>*/}
                {/*) : (*/}
                {/*  <></>*/}
                {/*)}*/}
              </>
            ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
              <>
                <Text style={styles.exTitle}>{type}</Text>
                {results.map((r, i) => (
                  <>
                    <Text key={i} style={styles.text}>{`${r.subject?.value} ${
                      r.result ? `(${r.result})` : ''
                    }`}</Text>
                  </>
                ))}
              </>
            ) : type === 'Other' ? (
              <>
                <Text style={styles.exTitle}>{title ?? ''}</Text>
                {results.map((r, i) => (
                  <>
                    <Text key={i} style={styles.text}>{`${r.subject?.value} ${
                      r.result ? `(${r.result})` : ''
                    }`}</Text>
                  </>
                ))}
              </>
            ) : (
              results.map((r, i) => (
                <Text key={i} style={[styles.exTitle, { paddingTop: 5 }]}>{`${r.subject?.value}${
                  r.result ? `, ${r.result}` : ''
                }`}</Text>
              ))
            )}
          </View>
          <View>{institute && <Text style={styles.textInstitute}>{institute.value}</Text>}</View>
        </View>
        <View style={styles.durationContainer}>
          <Text style={styles.dateText}>{duration[0]}</Text>
          <Text style={styles.dateText}>{duration[1]}</Text>
        </View>
      </View>

      {/*{type !== 'Certification' && <Text style={styles.timeline}>{duration[0]}</Text>}*/}
      {/*{type === 'Certification' && value?.period && (*/}
      {/*  <Text style={styles.timeline}>{duration[1]}</Text>*/}
      {/*)}*/}
      {description && type === 'Other' && (
        <View style={{ marginTop: 4 }}>
          {/*<Text style={styles.text}>Activities and Societies</Text>*/}
          <PdfHtml level={2}>
            {description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
      {achievements && (
        <View style={{ marginTop: 4 }}>
          {/*<Text style={styles.text}>Activities and Societies</Text>*/}
          <PdfHtml level={2}>
            {achievements.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
    </View>
  );
};
// const ActivitySocityView: FC<{ value: Education; selectedColor?: string; title?: boolean }> = ({
//   value,
//   selectedColor,
//   title,
// }) => {
//   const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
//   const { institute, description } = value;
//   return (
//     <View wrap={false} style={{ marginTop: 1 }}>
//       {title && (
//         <>
//           <Text style={styles.title}>Activities &</Text>
//           <Text style={styles.title}>Societies</Text>
//         </>
//       )}
//       {institute && description && <Text style={styles.exTitle}>{institute}</Text>}
//       {description && (
//         <PdfHtml>{description.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}</PdfHtml>
//       )}
//     </View>
//   );
// };
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const data = value.map((item, index) => {
    return (
      <View key={index} style={{ marginTop: 10 }}>
        {index === 0 && <Text style={styles.title}>Activities & Societies</Text>}
        {item.institute && item.description && (
          <Text style={[styles.exTitle, { marginTop: 20 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <PdfHtml key={v}>
                {v.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
              </PdfHtml>
            );
          }
        })}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.row}>
      <View style={{ width: 20, marginRight: 15 }}>
        <Image src={img} style={{ width: iconWidth ?? 17, height: iconHeight ?? 17, padding: 1 }} />
      </View>
      <View>
        {isUrl ? (
          <Link wrap style={[styles.contactText, { textDecoration: 'none' }]} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={[styles.emailText]} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.contactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View>
      <Text style={styles.refName}>{Utils.getFullName(value.firstName, value.lastName)}</Text>
      <Text style={styles.text}>{value.designation}</Text>
      <Text style={styles.text}>{value.organization}</Text>
      <View style={{ flexDirection: 'row' }}>
        <Text style={styles.boldText}>Mobile: </Text>
        <Text style={styles.text}>{value.mobile}</Text>
      </View>
      <View style={{ flexDirection: 'row' }}>
        <Text style={styles.boldText}>Email: </Text>
        <Text style={styles.text}>{value.email}</Text>
      </View>
    </View>
  );
};
const Template18 = (props: Props) => {
  const styles = useMemo(() => pageStyles({ color: props.selectedColor }), [props.selectedColor]);
  const { profile } = props;
  const personalStatement = useMemo(() => {
    const s1 = `I am on the lookout for a ${profile?.jobType
      ?.join(', ')
      ?.toLowerCase()}, ${profile?.jobCategory?.join(', ')?.toLowerCase()} opportunity as a ${
      profile?.desiredJobTitle?.value
    }${
      [...(profile?.sector ?? [])].length > 0
        ? `, in the ${profile?.sector?.join(', ').replace(/, ([^,]*)$/, ' and $1')} ${
            profile?.sector?.length === 1 ? 'sector' : 'sectors'
          }. \n`
        : ''
    }`;
    if (!profile?.personalStatement) {
      return `${profile?.jobType ? s1 : ''}`;
    }
    return `${profile?.jobType ? s1 : ''}${profile?.personalStatement
      ?.replace(/<p><br><\/p>/g, '')
      .replace(/em>/g, 'i>')}`;
  }, [
    profile?.desiredJobTitle,
    profile?.jobCategory,
    profile?.jobType,
    profile?.personalStatement,
    profile?.sector,
  ]);
  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View style={{ flexDirection: 'column', marginTop: 30, marginLeft: 30, marginRight: 30 }}>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.name}>{profile.firstName} </Text>
            {profile.lastName && <Text style={styles.name}>{profile.lastName}</Text>}
          </View>
          {profile?.personalStatementHeader && (
            <View style={styles.designationContainer}>
              <Text style={styles.designation}>
                {profile.personalStatementHeader}
                {profile.country ? ` | ${profile.country}` : ''}
              </Text>
            </View>
          )}
        </View>
        <View style={{ flexDirection: 'row' }}>
          <View style={styles.colomn1}>
            {/*<View*/}
            {/*  fixed*/}
            {/*  render={({ pageNumber }) => {*/}
            {/*    if (pageNumber !== 1) {*/}
            {/*      return <View style={{ height: 20 }} />;*/}
            {/*    }*/}
            {/*    return <View />;*/}
            {/*  }}*/}
            {/*/>*/}

            <View>
              <Text style={styles.title}>Contact</Text>
              {profile?.mobile && (
                <ContactDetailView
                  value={profile.mobile}
                  img={`${config.frontendUrl}/phone.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.residentialAddress && (
                <ContactDetailView
                  value={profile.residentialAddress}
                  img={`${config.frontendUrl}/locationPin.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.email && (
                <ContactDetailView
                  title={profile.email}
                  type={'email'}
                  value={`mailto:${profile.email}`}
                  img={`${config.frontendUrl}/email.png`}
                  selectedColor={props.selectedColor}
                  iconHeight={15}
                />
              )}
              {profile?.linkedInUrl && (
                <ContactDetailView
                  isUrl={true}
                  title={'Linkedin.com'}
                  value={profile.linkedInUrl}
                  img={`${config.frontendUrl}/linkedin.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.userId && (
                <ContactDetailView
                  title={'Careers360.com'}
                  isUrl={true}
                  value={`${config.frontendUrl}/profile/${profile.userId}`}
                  img={`${config.frontendUrl}/careers360.png`}
                  selectedColor={props.selectedColor}
                />
              )}
              {profile?.websiteUrl && profile?.websiteUrl?.length > 0 ? (
                <View style={{ marginTop: 20 }}>
                  <Text style={[styles.linkTitle, { marginBottom: 6 }]}>Other Links</Text>
                  {[...(profile?.websiteUrl ?? [])].map((url, index) => (
                    <Link key={index} src={url} style={{ textDecoration: 'none' }}>
                      <Text style={[styles.linkText, { paddingTop: 3 }]}>
                        {new URL(url).hostname.replace('www.', '')}
                      </Text>
                    </Link>
                  ))}
                </View>
              ) : (
                <></>
              )}
              <View wrap={false} style={{ marginTop: 20 }}>
                <Text style={[styles.linkTitle, { marginLeft: 2 }]}>All Links</Text>
                <View style={{ paddingTop: 5 }}>
                  <Image
                    style={{ width: 80, height: 80, marginLeft: -5 }}
                    source={QRCode.toDataURL(
                      `${config.frontendUrl}/profile/${profile?.userId}/links`,
                    )}
                  />
                </View>
              </View>
            </View>
            {profile?.languages && profile?.languages.length > 0 ? (
              <View style={{ paddingTop: 20 }}>
                {profile?.languages.map((lang, index) => (
                  <View key={index} wrap={false}>
                    {index === 0 ? <Text style={styles.title}>Languages</Text> : <></>}
                    <View
                      wrap={false}
                      style={{ flexDirection: 'row', fontSize: 13, marginTop: 16 }}>
                      <Text style={{ color: '#7E7E7E' }}>{'\u2022'}</Text>
                      <Text style={{ flex: 1, paddingLeft: 5, color: '#1E1313' }}>
                        {lang.language} : {lang.fluency}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            ) : (
              <></>
            )}
            {[...(profile.skills ?? [])].length > 0 ? (
              <View style={{ paddingTop: 20, flexDirection: 'row', flexWrap: 'wrap' }}>
                {[...(profile.skills ?? [])].map((skill, index) => (
                  <View wrap={false} key={index} style={{ alignItems: 'flex-start' }}>
                    {index === 0 && (
                      <Text style={[styles.title, { marginBottom: 11, paddingRight: 50 }]}>
                        Skills & Expertise
                      </Text>
                    )}
                    {/*<View wrap={false} key={index} style={styles.tag}>*/}
                    <Text style={[styles.tag, { color: '#01200C', fontSize: 12 }]}>
                      {skill.value}
                    </Text>
                    {/*</View>*/}
                  </View>
                ))}
              </View>
            ) : (
              <></>
            )}
            {isShowActivity ? (
              <View style={{ paddingTop: 15 }}>
                {/*{[...(profile.education ?? [])]*/}
                {/*  .filter((item) => !!item.description)*/}
                {/*  .map((item, index) => (*/}
                {/*    <ActivitySocityView*/}
                {/*      key={index}*/}
                {/*      title={index === 0}*/}
                {/*      value={item}*/}
                {/*      selectedColor={props.selectedColor}*/}
                {/*    />*/}
                {/*  ))}*/}
                <ActivitySocityView
                  value={profile.activities}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
            {profile.volunteering && profile.volunteering.length != 0 ? (
              <View style={{ paddingTop: 22 }}>
                <VolunteeringView
                  value={profile.volunteering}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
            {profile.organizationexp && profile.organizationexp.length != 0 ? (
              <View style={{ paddingTop: 22 }}>
                <OrganizationView
                  value={profile.organizationexp}
                  selectedColor={props.selectedColor}
                />
              </View>
            ) : (
              <></>
            )}
          </View>
          <View style={styles.colomn2}>
            <View>
              <View
                fixed
                render={({ pageNumber }) => {
                  if (pageNumber !== 1) {
                    return <View style={{ height: 20 }} />;
                  }
                  return <View />;
                }}
              />
              {isShowSummery ? (
                <View>
                  <Text style={styles.title}>Summary</Text>
                  <View style={{ marginTop: 10 }}>
                    <PdfHtml fontSize={'12px'}>
                      {personalStatement
                        .replace(/<li><br><\/li>/g, '')
                        .replace(/<p><br><\/p>/g, '')}
                    </PdfHtml>
                  </View>
                </View>
              ) : (
                <></>
              )}
              {[...(profile.experience ?? [])].length ? (
                <View style={{ paddingTop: 26 }}>
                  <Text style={styles.title}>Experience</Text>
                  {[...(profile.experience ?? [])].map((item, index) => (
                    <ExpCard key={index} value={item} selectedColor={props.selectedColor} />
                  ))}
                </View>
              ) : (
                <></>
              )}

              {[...(profile.education ?? [])].length > 0 ? (
                <View style={{ paddingTop: 26 }}>
                  {[...(profile.education ?? [])].map((item, index) => (
                    <View key={index} wrap={false}>
                      {index === 0 ? <Text style={styles.title}>Education</Text> : <></>}
                      <EduCard key={index} value={item} selectedColor={props.selectedColor} />
                    </View>
                  ))}
                </View>
              ) : (
                <></>
              )}
              {[...(profile.reference ?? [])].length > 0 ? (
                <View style={{ paddingBottom: 16 }}>
                  {[...(profile.reference ?? [])].map((item, index) => (
                    <View key={index} wrap={false}>
                      {index === 0 ? <Text style={styles.title}>References</Text> : <></>}
                      <ReferenceView key={index} value={item} selectedColor={props.selectedColor} />
                    </View>
                  ))}
                </View>
              ) : (
                <></>
              )}
            </View>
          </View>
        </View>
        <Text
          fixed
          style={styles.staticContent}
          render={({ pageNumber, totalPages }) => {
            if (pageNumber === totalPages) {
              return profile?.reference ? '' : 'References will be provided upon request';
            }
            return '';
          }}
        />
      </Page>
    </Document>
  );
};
export default Template18;
